import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  addVaultUrlReference,
  deleteVaultDocument,
  fetchVaultDocuments,
  uploadVaultFile,
  type VaultDocument,
} from './vault-api';

/**
 * Vault query + mutation hooks (SCRUM-382). One shared query key; every
 * mutation invalidates it so the table refreshes from the server (the list is
 * small — no optimistic bookkeeping needed).
 */
const VAULT_KEY = ['vault'] as const;

export function useVaultDocuments(): {
  documents: VaultDocument[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<VaultDocument[]>({
    queryKey: VAULT_KEY,
    queryFn: fetchVaultDocuments,
    staleTime: 30_000,
    retry: false,
  });
  return { documents: data ?? [], isLoading, isError };
}

export interface StagedUrl {
  name: string;
  url: string;
}

/**
 * Commits a staged upload set (files + URL references) sequentially, then
 * refreshes the list once. Sequential (not Promise.all) keeps the demo-scale
 * requests simple and the failure report deterministic: the first failure
 * aborts the remainder and surfaces its server message.
 */
export interface VaultUploadInput {
  files: File[];
  urls: StagedUrl[];
  uploadedBy: string;
}

export function useVaultUpload(): UseMutationResult<VaultDocument[], Error, VaultUploadInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, urls, uploadedBy }) => {
      const created: VaultDocument[] = [];
      for (const file of files) {
        created.push(await uploadVaultFile(file, uploadedBy));
      }
      for (const u of urls) {
        created.push(await addVaultUrlReference(u.name, u.url, uploadedBy));
      }
      return created;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}

export function useVaultDelete(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVaultDocument(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: VAULT_KEY }),
  });
}
